// =============================================================================
// UniquePagesPanel — krok "Podstrony" (faza Ulepszanie zaplecza).
// Propozycja tematów → edycja → generacja (pojedynczo/bulk) → edytor → publikacja WP Page.
// Sekcja Autor/persona wydzielona do AuthorInfoPanel (osobna kropka).
// =============================================================================

import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, Plus, Wand2, Rocket, Trash2, Loader2, Eye, ExternalLink, Save } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useUniqueSubpages } from "@/hooks/useUniqueSubpages";
import { useUniquePagesBulk } from "@/hooks/useUniquePagesBulk";
import type { ThematicMap } from "@/types/thematicMaps";
import type { UniqueSubpage } from "@/types/uniquePages";

interface Props {
  map: ThematicMap;
}

export function UniquePagesPanel({ map }: Props) {
  const websiteId = (map as any).website_id as string | null;
  return (
    <div className="space-y-4">
      <div>
        <h3 className="flex items-center gap-2 text-base font-semibold">
          <Sparkles className="h-4 w-4" /> Unikalne podstrony
        </h3>
        <p className="text-sm text-muted-foreground">
          Strony budujące wrażenie serwisu prowadzonego przez realną osobę (O nas / Metodologia / Polityka
          redakcyjna...) — generowane inaczej niż wpisy, z publikacją jako <span className="font-medium">strona WP</span>.
        </p>
      </div>
      <SubpagesSection mapId={map.id} websiteId={websiteId} />
    </div>
  );
}

function SubpagesSection({ mapId, websiteId }: { mapId: string; websiteId: string | null }) {
  const { toast } = useToast();
  const sp = useUniqueSubpages(mapId);
  const bulk = useUniquePagesBulk();
  const [proposing, setProposing] = useState(false);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newTopic, setNewTopic] = useState("");
  const [editor, setEditor] = useState<UniqueSubpage | null>(null);

  useEffect(() => { if (bulk.summary) void sp.refresh(); /* eslint-disable-next-line */ }, [bulk.summary]);

  const drafts = useMemo(() => sp.subpages.filter((s) => s.status === "draft_topic"), [sp.subpages]);

  const handlePropose = async () => {
    setProposing(true);
    try {
      const res = await sp.propose();
      if (res.status === "ok") toast({ title: "Gotowe", description: `Zaproponowano ${res.count} podstron.` });
      else toast({ title: res.status === "skipped" ? "Pominięto" : "Błąd", description: res.reason || "", variant: "destructive" });
    } finally { setProposing(false); }
  };

  const handleAdd = async () => {
    if (!newTitle.trim()) return;
    await sp.addManual(newTitle, newTopic);
    setNewTitle(""); setNewTopic(""); setAdding(false);
  };

  const handleBulk = () => {
    const items = drafts.map((d) => ({ subpageId: d.id, label: d.title }));
    if (!items.length) { toast({ title: "Brak draftów do wygenerowania" }); return; }
    void bulk.start(items, 2);
  };

  const handleGenerate = async (id: string) => {
    const res = await sp.generate(id);
    if (res.status !== "ok") toast({ title: res.status === "skipped" ? "Pominięto" : "Błąd", description: res.reason || "", variant: "destructive" });
  };

  const handlePublish = async (id: string) => {
    if (!websiteId) { toast({ title: "Brak domeny", description: "Mapa nie ma przypisanej domeny.", variant: "destructive" }); return; }
    const res = await sp.publish(id, websiteId);
    if (res.ok) toast({ title: "Opublikowano stronę WP", description: res.url || "" });
    else toast({ title: "Błąd publikacji", description: res.error || "", variant: "destructive" });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={handlePropose} disabled={proposing}>
          {proposing ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
          Zaproponuj tematy
        </Button>
        <Button size="sm" variant="outline" onClick={() => setAdding((v) => !v)}>
          <Plus className="mr-1.5 h-3.5 w-3.5" />Dodaj ręcznie
        </Button>
        {drafts.length > 0 && (
          <Button size="sm" variant="outline" onClick={handleBulk} disabled={bulk.running}>
            {bulk.running ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1.5 h-3.5 w-3.5" />}
            Generuj wszystkie drafty ({drafts.length})
          </Button>
        )}
      </div>

      {bulk.running && (
        <div className="rounded-md border border-border/60 bg-muted/30 px-3 py-2 text-xs">
          Generowanie: {bulk.progress.current}/{bulk.progress.total} · ✅ {bulk.progress.ok} · ⏭ {bulk.progress.skipped} · ✗ {bulk.progress.failed}
          <Button size="sm" variant="ghost" className="ml-2 h-6" onClick={bulk.stop}>Stop</Button>
        </div>
      )}

      {adding && (
        <div className="space-y-2 rounded-md border border-border/60 p-3">
          <Input placeholder="Tytuł podstrony (np. O nas)" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} />
          <Input placeholder="O czym (krótki brief)" value={newTopic} onChange={(e) => setNewTopic(e.target.value)} />
          <div className="flex gap-2">
            <Button size="sm" onClick={handleAdd} disabled={!newTitle.trim()}>Dodaj</Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Anuluj</Button>
          </div>
        </div>
      )}

      {sp.loading ? (
        <p className="text-sm text-muted-foreground">Ładowanie...</p>
      ) : sp.subpages.length === 0 ? (
        <p className="text-sm text-muted-foreground">Brak podstron. Kliknij „Zaproponuj tematy" albo dodaj ręcznie.</p>
      ) : (
        <div className="space-y-1.5">
          {sp.subpages.map((s) => (
            <SubpageRow
              key={s.id}
              s={s}
              busy={sp.busyId === s.id}
              onUpdate={(f) => sp.updateFields(s.id, f)}
              onGenerate={() => handleGenerate(s.id)}
              onPublish={() => handlePublish(s.id)}
              onRemove={() => sp.remove(s.id)}
              onEdit={() => setEditor(s)}
            />
          ))}
        </div>
      )}

      <SubpageEditorDialog
        subpage={editor}
        onClose={() => setEditor(null)}
        onSave={async (html) => { if (editor) { await sp.updateFields(editor.id, { html }); } setEditor(null); }}
      />
    </div>
  );
}

function statusBadge(status: string) {
  if (status === "ready") return <Badge className="bg-blue-500/15 text-blue-600 dark:text-blue-300">gotowe</Badge>;
  if (status === "published") return <Badge className="bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">opublikowane</Badge>;
  if (status === "generating") return <Badge variant="secondary">generowanie…</Badge>;
  return <Badge variant="outline">temat</Badge>;
}

function SubpageRow(props: {
  s: UniqueSubpage;
  busy: boolean;
  onUpdate: (f: { title?: string; topic?: string }) => void;
  onGenerate: () => void;
  onPublish: () => void;
  onRemove: () => void;
  onEdit: () => void;
}) {
  const { s, busy, onUpdate, onGenerate, onPublish, onRemove, onEdit } = props;
  const [title, setTitle] = useState(s.title);
  const [topic, setTopic] = useState(s.topic ?? "");
  useEffect(() => { setTitle(s.title); setTopic(s.topic ?? ""); }, [s.title, s.topic]);
  const isDraft = s.status === "draft_topic";

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border/60 px-3 py-2">
      <div className="min-w-0 flex-1 space-y-1">
        {isDraft ? (
          <>
            <Input className="h-7 text-sm" value={title} onChange={(e) => setTitle(e.target.value)}
              onBlur={() => title !== s.title && onUpdate({ title })} />
            <Input className="h-7 text-xs" placeholder="O czym…" value={topic} onChange={(e) => setTopic(e.target.value)}
              onBlur={() => topic !== (s.topic ?? "") && onUpdate({ topic })} />
          </>
        ) : (
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium">{s.title}</span>
            {statusBadge(s.status)}
            {s.wp_page_url && (
              <a href={s.wp_page_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary" title="Otwórz na WP">
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
          </div>
        )}
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {isDraft && (
          <Button size="sm" className="h-7 text-xs" onClick={onGenerate} disabled={busy}>
            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Wand2 className="mr-1 h-3.5 w-3.5" />}Generuj
          </Button>
        )}
        {(s.status === "ready" || s.status === "published") && (
          <>
            <Button size="sm" variant="ghost" className="h-7 w-7 p-0" title="Podgląd / edycja" onClick={onEdit}><Eye className="h-3.5 w-3.5" /></Button>
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={onPublish} disabled={busy} title="Publikuj jako strona na WP">
              {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Rocket className="mr-1 h-3.5 w-3.5" />}
              {s.status === "published" ? "Publikuj ponownie" : "Publikuj"}
            </Button>
          </>
        )}
        <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-destructive" title="Usuń" onClick={onRemove}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function SubpageEditorDialog(props: {
  subpage: UniqueSubpage | null;
  onClose: () => void;
  onSave: (html: string) => Promise<void>;
}) {
  const { subpage, onClose, onSave } = props;
  const [html, setHtml] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setHtml(subpage?.html ?? ""); }, [subpage]);

  return (
    <Dialog open={!!subpage} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader><DialogTitle className="truncate pr-6">{subpage?.title}</DialogTitle></DialogHeader>
        <Tabs defaultValue="preview">
          <TabsList>
            <TabsTrigger value="preview">Podgląd</TabsTrigger>
            <TabsTrigger value="html">HTML</TabsTrigger>
          </TabsList>
          <TabsContent value="preview">
            <div className="prose prose-sm dark:prose-invert max-h-[55vh] max-w-none overflow-auto rounded-md border border-border/60 p-4"
              // eslint-disable-next-line react/no-danger
              dangerouslySetInnerHTML={{ __html: html }} />
          </TabsContent>
          <TabsContent value="html">
            <Textarea className="min-h-[45vh] font-mono text-xs" value={html} onChange={(e) => setHtml(e.target.value)} />
          </TabsContent>
        </Tabs>
        <div className="flex justify-end gap-2">
          <Button size="sm" variant="ghost" onClick={onClose}>Zamknij</Button>
          <Button size="sm" disabled={saving} onClick={async () => { setSaving(true); try { await onSave(html); } finally { setSaving(false); } }}>
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Save className="mr-1.5 h-3.5 w-3.5" />}Zapisz
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
